import {z} from 'zod';

import {BaseVoidResponseSchema} from '@dms/api/shared';

// ====================
// AI Vehicle Service Intervals Types
// ====================

export const AiVehicleServiceIntervalsStatusSchema = z.enum(['processing', 'completed', 'failed']);

export type AiVehicleServiceIntervalsStatus = z.infer<typeof AiVehicleServiceIntervalsStatusSchema>;

export const ExpectedNextVehicleServiceIntervalRequestBodySchema = z.object({
  nextDate: z.string().nullable(),
  nextMileage: z.number().int().nullable(),
});

export type ExpectedNextVehicleServiceIntervalRequestBody = z.infer<
  typeof ExpectedNextVehicleServiceIntervalRequestBodySchema
>;

export const ExpectedVehicleServiceIntervalRequestBodySchema = z.object({
  nextDate: z.string().nullable(),
  nextMileage: z.number().int().nullable(),
  afterMonths: z.number().int().nullable(),
  afterMileage: z.number().int().nullable(),
});

export type ExpectedVehicleServiceIntervalRequestBody = z.infer<
  typeof ExpectedVehicleServiceIntervalRequestBodySchema
>;

export const ExpectedAiVehicleServiceIntervalsAnnualMileageRequestBodySchema = z.object({
  calculatedFromServiceVisits: z.number().int().nullable(),
});

export type ExpectedAiVehicleServiceIntervalsAnnualMileageRequestBody = z.infer<
  typeof ExpectedAiVehicleServiceIntervalsAnnualMileageRequestBodySchema
>;

export const ExpectedVehicleServiceIntervalValuesRequestBodySchema = z.object({
  serviceInspection: ExpectedVehicleServiceIntervalRequestBodySchema,
  engineOil: ExpectedVehicleServiceIntervalRequestBodySchema,
  gearboxOil: ExpectedVehicleServiceIntervalRequestBodySchema,
  fourByFourOilDistributor: ExpectedVehicleServiceIntervalRequestBodySchema,
  timingBelt: ExpectedVehicleServiceIntervalRequestBodySchema,
  brakeFluid: ExpectedNextVehicleServiceIntervalRequestBodySchema,
  coolantFluid: ExpectedNextVehicleServiceIntervalRequestBodySchema,
  annualMileage: ExpectedAiVehicleServiceIntervalsAnnualMileageRequestBodySchema,
});

export type ExpectedVehicleServiceIntervalValuesRequestBody = z.infer<
  typeof ExpectedVehicleServiceIntervalValuesRequestBodySchema
>;

export const CompleteAiVehicleServiceIntervalRequestBodySchema = z.object({
  values: ExpectedVehicleServiceIntervalValuesRequestBodySchema,
});

export type CompleteAiVehicleServiceIntervalRequestBody = z.infer<
  typeof CompleteAiVehicleServiceIntervalRequestBodySchema
>;

export const ExpectedNextVehicleServiceIntervalResponseBodySchema = z.object({
  nextDate: z.string().nullable(),
  nextMileage: z.number().int().nullable(),
});

export type ExpectedNextVehicleServiceIntervalResponseBody = z.infer<
  typeof ExpectedNextVehicleServiceIntervalResponseBodySchema
>;

export const ExpectedVehicleServiceIntervalResponseBodySchema = z.object({
  nextDate: z.string().nullable(),
  nextMileage: z.number().int().nullable(),
  afterMonths: z.number().int().nullable(),
  afterMileage: z.number().int().nullable(),
});

export type ExpectedVehicleServiceIntervalResponseBody = z.infer<
  typeof ExpectedVehicleServiceIntervalResponseBodySchema
>;

export const ExpectedAiVehicleServiceIntervalsAnnualMileageResponseBodySchema = z.object({
  calculatedFromServiceVisits: z.number().int().nullable(),
});

export type ExpectedAiVehicleServiceIntervalsAnnualMileageResponseBody = z.infer<
  typeof ExpectedAiVehicleServiceIntervalsAnnualMileageResponseBodySchema
>;

export const ExpectedVehicleServiceIntervalValuesResponseBodySchema = z.object({
  serviceInspection: ExpectedVehicleServiceIntervalResponseBodySchema,
  engineOil: ExpectedVehicleServiceIntervalResponseBodySchema,
  gearboxOil: ExpectedVehicleServiceIntervalResponseBodySchema,
  fourByFourOilDistributor: ExpectedVehicleServiceIntervalResponseBodySchema,
  timingBelt: ExpectedVehicleServiceIntervalResponseBodySchema,
  brakeFluid: ExpectedNextVehicleServiceIntervalResponseBodySchema,
  coolantFluid: ExpectedNextVehicleServiceIntervalResponseBodySchema,
  annualMileage: ExpectedAiVehicleServiceIntervalsAnnualMileageResponseBodySchema,
});

export type ExpectedVehicleServiceIntervalValuesResponseBody = z.infer<
  typeof ExpectedVehicleServiceIntervalValuesResponseBodySchema
>;

export const AiVehicleServiceIntervalsResponseBodySchema = z.object({
  id: z.string().uuid(),
  vehicleId: z.string().uuid(),
  initializedAt: z.string(),
  status: AiVehicleServiceIntervalsStatusSchema,
  values: ExpectedVehicleServiceIntervalValuesResponseBodySchema,
});

export type AiVehicleServiceIntervalsResponseBody = z.infer<
  typeof AiVehicleServiceIntervalsResponseBodySchema
>;

export const UuidResponseBodySchema = z.object({
  id: z.string().uuid(),
});

export type UuidResponseBody = z.infer<typeof UuidResponseBodySchema>;

// ====================
// Vehicle Service Intervals Types
// ====================

export const AiVehicleServiceIntervalsServiceInspectionSchema = z.object({
  lastServiceInspectionDate: z.string().nullable(),
  lastServiceInspectionMileage: z.number().int().nullable(),
  nextServiceInspectionDate: z.string().nullable(),
  nextServiceInspectionMileage: z.number().int().nullable(),
  replacementAfterMonths: z.number().int().nullable(),
  replacementAfterMileage: z.number().int().nullable(),
});

export type AiVehicleServiceIntervalsServiceInspection = z.infer<
  typeof AiVehicleServiceIntervalsServiceInspectionSchema
>;

export const AiVehicleServiceIntervalsEngineOilSchema = z.object({
  lastReplacementDate: z.string().nullable(),
  lastReplacementMileage: z.number().int().nullable(),
  nextReplacementDate: z.string().nullable(),
  nextReplacementMileage: z.number().int().nullable(),
  replacementAfterMonths: z.number().int().nullable(),
  replacementAfterMileage: z.number().int().nullable(),
});

export type AiVehicleServiceIntervalsEngineOil = z.infer<
  typeof AiVehicleServiceIntervalsEngineOilSchema
>;

export const AiVehicleServiceIntervalsGearboxOilSchema = z.object({
  lastReplacementDate: z.string().nullable(),
  lastReplacementMileage: z.number().int().nullable(),
  nextReplacementDate: z.string().nullable(),
  nextReplacementMileage: z.number().int().nullable(),
  replacementAfterMonths: z.number().int().nullable(),
  replacementAfterMileage: z.number().int().nullable(),
});

export type AiVehicleServiceIntervalsGearboxOil = z.infer<
  typeof AiVehicleServiceIntervalsGearboxOilSchema
>;

export const AiVehicleServiceIntervalsFourByFourOilDistributorSchema = z.object({
  lastReplacementDate: z.string().nullable(),
  lastReplacementMileage: z.number().int().nullable(),
  nextReplacementDate: z.string().nullable(),
  nextReplacementMileage: z.number().int().nullable(),
  replacementAfterMonths: z.number().int().nullable(),
  replacementAfterMileage: z.number().int().nullable(),
});

export type AiVehicleServiceIntervalsFourByFourOilDistributor = z.infer<
  typeof AiVehicleServiceIntervalsFourByFourOilDistributorSchema
>;

export const AiVehicleServiceIntervalsTimingBeltSchema = z.object({
  lastReplacementDate: z.string().nullable(),
  lastReplacementMileage: z.number().int().nullable(),
  nextReplacementDate: z.string().nullable(),
  nextReplacementMileage: z.number().int().nullable(),
  replacementAfterMonths: z.number().int().nullable(),
  replacementAfterMileage: z.number().int().nullable(),
});

export type AiVehicleServiceIntervalsTimingBelt = z.infer<
  typeof AiVehicleServiceIntervalsTimingBeltSchema
>;

export const AiVehicleServiceIntervalsBrakeFluidSchema = z.object({
  lastReplacementDate: z.string().nullable(),
  lastReplacementMileage: z.number().int().nullable(),
  nextReplacementDate: z.string().nullable(),
  nextReplacementMileage: z.number().int().nullable(),
});

export type AiVehicleServiceIntervalsBrakeFluid = z.infer<
  typeof AiVehicleServiceIntervalsBrakeFluidSchema
>;

export const AiVehicleServiceIntervalsCoolantFluidSchema = z.object({
  lastReplacementDate: z.string().nullable(),
  lastReplacementMileage: z.number().int().nullable(),
  nextReplacementDate: z.string().nullable(),
  nextReplacementMileage: z.number().int().nullable(),
});

export type AiVehicleServiceIntervalsCoolantFluid = z.infer<
  typeof AiVehicleServiceIntervalsCoolantFluidSchema
>;

export const AiVehicleServiceIntervalsAnnualMileageSchema = z.object({
  calculatedFromServiceVisits: z.number().int().nullable(),
});

export type AiVehicleServiceIntervalsAnnualMileage = z.infer<
  typeof AiVehicleServiceIntervalsAnnualMileageSchema
>;

export const AiVehicleServiceIntervalsSchema = z.object({
  serviceInspection: AiVehicleServiceIntervalsServiceInspectionSchema,
  engineOil: AiVehicleServiceIntervalsEngineOilSchema,
  gearboxOil: AiVehicleServiceIntervalsGearboxOilSchema,
  fourByFourOilDistributor: AiVehicleServiceIntervalsFourByFourOilDistributorSchema,
  timingBelt: AiVehicleServiceIntervalsTimingBeltSchema,
  brakeFluid: AiVehicleServiceIntervalsBrakeFluidSchema,
  coolantFluid: AiVehicleServiceIntervalsCoolantFluidSchema,
  annualMileage: AiVehicleServiceIntervalsAnnualMileageSchema,
});

export type AiVehicleServiceIntervals = z.infer<typeof AiVehicleServiceIntervalsSchema>;

// ====================
// API Args
// ====================

export const CompleteAiVehicleServiceIntervalApiArgSchema = z.object({
  aiVehicleServiceIntervalId: z.string().uuid(),
  body: CompleteAiVehicleServiceIntervalRequestBodySchema,
});

export type CompleteAiVehicleServiceIntervalApiArg = z.infer<
  typeof CompleteAiVehicleServiceIntervalApiArgSchema
>;

export const CompleteAiVehicleServiceIntervalApiResponseSchema = BaseVoidResponseSchema;

export type CompleteAiVehicleServiceIntervalApiResponse = z.infer<
  typeof CompleteAiVehicleServiceIntervalApiResponseSchema
>;

export const FailAiVehicleServiceIntervalApiArgSchema = z.object({
  aiVehicleServiceIntervalId: z.string().uuid(),
});

export type FailAiVehicleServiceIntervalApiArg = z.infer<
  typeof FailAiVehicleServiceIntervalApiArgSchema
>;

export const FailAiVehicleServiceIntervalApiResponseSchema = BaseVoidResponseSchema;

export type FailAiVehicleServiceIntervalApiResponse = z.infer<
  typeof FailAiVehicleServiceIntervalApiResponseSchema
>;

export const GetLatestAiVehicleServiceIntervalsApiArgSchema = z.object({
  vehicleId: z.string().uuid(),
});

export type GetLatestAiVehicleServiceIntervalsApiArg = z.infer<
  typeof GetLatestAiVehicleServiceIntervalsApiArgSchema
>;

export const GetLatestAiVehicleServiceIntervalsApiResponseSchema =
  AiVehicleServiceIntervalsResponseBodySchema;

export type GetLatestAiVehicleServiceIntervalsApiResponse = z.infer<
  typeof GetLatestAiVehicleServiceIntervalsApiResponseSchema
>;

export const InitializeAiVehicleServiceIntervalsApiArgSchema = z.object({
  vehicleId: z.string().uuid(),
});

export type InitializeAiVehicleServiceIntervalsApiArg = z.infer<
  typeof InitializeAiVehicleServiceIntervalsApiArgSchema
>;

export const InitializeAiVehicleServiceIntervalsApiResponseSchema = UuidResponseBodySchema;

export type InitializeAiVehicleServiceIntervalsApiResponse = z.infer<
  typeof InitializeAiVehicleServiceIntervalsApiResponseSchema
>;
